/**
 * Le Mode Debug est-il ouvert en ce moment ?
 *
 * Sert à n'exposer les actions qui l'exigent (purge d'une fiche, purge de la
 * base) que quand elles sont réellement exécutables. Le serveur les refuse
 * déjà hors session — mais un bouton rouge affiché en permanence est une
 * promesse fausse : l'admin le découvre après avoir ouvert la modale et saisi
 * son mot de passe.
 *
 * Volontairement léger : une requête à l'affichage, pas le WebSocket du
 * tableau de bord Debug — celui-ci pousse un instantané toutes les deux
 * secondes et compte ses abonnés, l'ouvrir depuis une fiche entreprise
 * coûterait cher pour un booléen.
 *
 * Deux façons dont l'état change sans que cette page l'apprenne, toutes deux
 * couvertes :
 *   - la session est ouverte (ou coupée) depuis l'écran Mode Debug, souvent
 *     dans un autre onglet → on revalide au retour du focus ;
 *   - la session expire d'elle-même (≤ 60 min) → le serveur annonce le temps
 *     restant, on referme la porte à l'échéance sans rien redemander.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/crmApi";

export default function useDebugMode({ enabled = true } = {}) {
  // null = pas encore su. Distinct de false : rien ne doit clignoter au
  // premier rendu, et l'appelant peut vouloir traiter les deux différemment.
  const [active, setActive] = useState(null);
  const timerRef = useRef(null);
  const aliveRef = useRef(true);

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  const check = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/admin/debug/status");
      if (!aliveRef.current) return;
      const on = Boolean(data?.active);
      setActive(on);
      clearTimer();
      const remaining = Number(data?.session?.remaining_seconds);
      if (on && remaining > 0) {
        // +1 s : on referme après l'échéance, jamais un cheveu avant.
        timerRef.current = setTimeout(() => {
          if (aliveRef.current) setActive(false);
        }, (remaining + 1) * 1000);
      }
    } catch {
      // Route réservée à l'admin : un 403 signifie « pas de Mode Debug pour
      // vous », ce qui se traduit exactement par « porte fermée ».
      if (aliveRef.current) setActive(false);
    }
  }, [enabled]);

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) { setActive(false); return undefined; }
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      aliveRef.current = false;
      clearTimer();
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, check]);

  return { debugActive: active === true, debugKnown: active !== null, refresh: check };
}
